import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Workspace } from "../lib/api";
import { useConfirm } from "./ConfirmDialog";
import { EnvDot, EnvEditor } from "../pages/Environments";

interface Props {
  workspace: Workspace;
  open: boolean;
  // When set, the modal opens already focused on this env-var name and
  // asks the editor to scroll/flash that row. Used by the click-on-chip
  // path from VarText / ChipInput / cmVarHighlight.
  highlightVar?: string;
  onClose: () => void;
}

// Modal version of the Environments page. Same affordances as the page but
// rendered as an overlay so users can edit envs without leaving their
// current request. Selected env is held in local state — not in the URL —
// because the modal is transient.
export default function EnvironmentsModal({ workspace, open, highlightVar, onClose }: Props) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const list = useQuery({
    queryKey: ["environments", workspace.id],
    queryFn: () => api.environments.list(workspace.id),
    enabled: open,
  });
  const envs = list.data?.environments ?? [];

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // When the modal opens (or env list arrives), pick:
  //   1. an env that defines `highlightVar` (so the user lands on the
  //      one they can actually edit for that variable), else
  //   2. the workspace's default env, else
  //   3. the first one in the list.
  useEffect(() => {
    if (!open) return;
    if (selectedId && envs.some((e) => e.id === selectedId)) return;
    let pick: typeof envs[number] | undefined;
    if (highlightVar) {
      pick = envs.find((e) => Object.prototype.hasOwnProperty.call(e.vars, highlightVar));
    }
    pick = pick ?? envs.find((e) => e.is_default) ?? envs[0];
    setSelectedId(pick?.id ?? null);
  }, [open, envs, selectedId, highlightVar]);
  // When the modal re-opens with a new highlightVar, jump to whichever
  // env defines it.
  useEffect(() => {
    if (!open || !highlightVar) return;
    const owner = envs.find((e) => Object.prototype.hasOwnProperty.call(e.vars, highlightVar));
    if (owner && owner.id !== selectedId) setSelectedId(owner.id);
  }, [open, highlightVar, envs]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = useMemo(() => envs.find((e) => e.id === selectedId) ?? null, [envs, selectedId]);

  const create = useMutation({
    mutationFn: (opts: { is_personal: boolean }) =>
      api.environments.create({
        workspace_id: workspace.id,
        name: opts.is_personal ? "Personal" : "Untitled environment",
        color: null,
        is_personal: opts.is_personal,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["environments", workspace.id] });
      setSelectedId(r.id);
    },
  });
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  // Close on Escape, just like other modals.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-[color:var(--backdrop)]" onClick={onClose}>
      <div
        className="card w-full max-w-5xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Environments</h2>
            <span className="text-xs muted-strong">— {workspace.name}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>
        <div className="flex-1 min-h-0 flex">
          <aside className="w-56 shrink-0 border-r border-border bg-surface flex flex-col min-h-0">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border relative">
              <div className="text-[10px] uppercase tracking-wider muted-strong font-semibold">Environments</div>
              <button
                type="button"
                onClick={() => setCreateMenuOpen((v) => !v)}
                onBlur={() => setTimeout(() => setCreateMenuOpen(false), 120)}
                title="New environment"
                aria-label="New environment"
                aria-haspopup="menu"
                aria-expanded={createMenuOpen}
                disabled={create.isPending}
                className="h-6 w-6 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2 disabled:opacity-50"
              >＋</button>
              {createMenuOpen && (
                <div className="absolute right-2 top-full mt-1 z-30 card shadow-[var(--shadow-strong)] p-1 w-52 text-sm" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-surface-2"
                    onMouseDown={(e) => { e.preventDefault(); setCreateMenuOpen(false); create.mutate({ is_personal: false }); }}
                  >
                    <div>Shared environment</div>
                    <div className="text-[11px] muted-strong">Visible to everyone in this workspace.</div>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-surface-2"
                    onMouseDown={(e) => { e.preventDefault(); setCreateMenuOpen(false); create.mutate({ is_personal: true }); }}
                  >
                    <div>Personal (only you)</div>
                    <div className="text-[11px] muted-strong">Your secrets, never shared with teammates.</div>
                  </button>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto scroll-pane py-1">
              {envs.length === 0 && (
                <div className="p-3"><div className="empty-card">No environments yet. Hit <span className="empty-card-strong">＋</span> above to add one.</div></div>
              )}
              <ul>
                {envs.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(e.id)}
                      className={
                        "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left " +
                        (e.id === selectedId ? "bg-accent-soft text-accent-bright" : "text-text-dim hover:bg-surface-2 hover:text-text")
                      }
                    >
                      <EnvDot color={e.color} />
                      <span className="flex-1 truncate">{e.name}</span>
                      {e.is_personal && (
                        <span
                          className="chip bg-accent-soft text-accent-bright text-[10px] inline-flex items-center gap-1"
                          title="Personal — only visible to you"
                        >
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M7 11V8a5 5 0 0110 0v3M5 11h14v9H5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          personal
                        </span>
                      )}
                      {e.is_default && <span className="chip bg-surface-3 muted-strong text-[10px]">default</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
          <main className="flex-1 min-w-0 overflow-auto scroll-pane bg-bg p-5">
            {selected ? (
              <EnvEditor
                key={selected.id}
                env={selected}
                workspaceId={workspace.id}
                highlightVar={highlightVar}
                onDeleted={() => {
                  qc.invalidateQueries({ queryKey: ["environments", workspace.id] });
                  setSelectedId(null);
                }}
                onDuplicated={(newId) => setSelectedId(newId)}
                confirm={confirm}
              />
            ) : (
              <div className="grid h-full place-items-center muted">
                <p className="text-sm">Pick or create an environment to manage its variables.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
